import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { BookOpen, Search, ArrowRight, Sparkles } from 'lucide-react';
import api from '../../services/api';

export default function StudentSubjects() {
  const navigate = useNavigate();
  const [grades, setGrades] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');

  useEffect(() => { loadSubjects(); }, []);

  const loadSubjects = async () => {
    try {
      const subRes = await api.get('/student/subjects');
      if (subRes.data.success && subRes.data.data) {
        // Support new format: data.grades (array of {grade_id, grade_name, subjects: []})
        if (Array.isArray(subRes.data.data.grades)) {
          setGrades(subRes.data.data.grades || []);
        } else if (Array.isArray(subRes.data.data.subjects)) {
          // Fallback for older format: group into single grade
          const subjects = subRes.data.data.subjects || [];
          const grouped = {};
          subjects.forEach(s => {
            const gid = s.grade_id || 0;
            if (!grouped[gid]) grouped[gid] = { grade_id: gid, grade_name: s.grade_name || '', subjects: [] };
            grouped[gid].subjects.push(s);
          });
          setGrades(Object.values(grouped));
        }
      }
    } catch (err) {
    } finally {
      setLoading(false);
    }
  };

  const filteredGrades = grades.map(g => ({
    ...g,
    subjects: (g.subjects || []).filter(s => s.subject_name.toLowerCase().includes(search.toLowerCase()))
  })).filter(g => (g.subjects || []).length > 0);

  // Flatten subjects for summary/counting and reuse in the summary block
  const filteredSubjects = filteredGrades.flatMap(g => g.subjects || []);

  const formatGradeName = (value) => {
    const name = String(value || '').trim();
    return name.replace(/^Grade\s+/i, '');
  };

  const cardTones = [
    'bg-slate-50/90 border-slate-200',
    'bg-sky-50/90 border-sky-200',
    'bg-violet-50/90 border-violet-200',
    'bg-emerald-50/90 border-emerald-200',
    'bg-rose-50/90 border-rose-200',
    'bg-blue-50/90 border-blue-200',
  ];

  const iconTones = [
    'from-slate-500 to-slate-400',
    'from-sky-500 to-cyan-400',
    'from-indigo-500 to-violet-400',
    'from-emerald-500 to-teal-400',
    'from-rose-500 to-pink-400',
    'from-blue-500 to-indigo-400',
  ];

  // Header gradients with soft glassy tone.
  const headerGradients = [
    'from-slate-200 to-indigo-100',
    'from-blue-200 to-sky-100',
    'from-emerald-200 to-teal-100',
    'from-zinc-200 to-slate-100',
    'from-rose-200 to-pink-100',
    'from-indigo-200 to-violet-100',
  ];

  if (loading) {
    return <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div></div>;
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-gray-800 flex items-center gap-2">My Subjects <Sparkles className="w-5 h-5 text-indigo-500" /></h1>
        <p className="text-base text-gray-600">Subjects you are enrolled in this term</p>
      </div>

      {/* Search */}
      <div className="bg-white/75 rounded-2xl shadow-sm border border-gray-100 p-5 sm:p-6">
        <div className="relative max-w-md">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4 sm:w-5 sm:h-5" />
          <input type="text" placeholder="Search subjects..." value={search} onChange={(e) => setSearch(e.target.value)}
            className="w-full pl-10 pr-4 py-3.5 text-base border border-gray-200 rounded-xl focus:ring-2 focus:ring-indigo-500" />
        </div>
      </div>

{filteredGrades.length === 0 ? (
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 sm:p-12 text-center">
          <BookOpen className="w-10 h-10 sm:w-12 sm:h-12 text-gray-300 mx-auto mb-4" />
          <h3 className="text-base sm:text-lg font-medium text-gray-600">No subjects found</h3>
          <p className="text-sm sm:text-base text-gray-400 mt-2">No subjects have been assigned to your grades yet</p>
        </div>
      ) : (
        <div className="space-y-6">
          {filteredGrades.map((grade, gIdx) => (
            <div key={grade.grade_id}>
              <div className={`bg-gradient-to-r ${headerGradients[gIdx % headerGradients.length]} rounded-2xl px-4 py-2.5 text-slate-700 mb-3 shadow-sm border border-white/80 backdrop-blur-sm flex items-center gap-3`}>
                <span className="inline-flex h-7 min-w-7 items-center justify-center rounded-lg bg-white/70 px-2 text-xs font-bold text-slate-600">{String(gIdx + 1).padStart(2, '0')}</span>
                <h3 className="font-semibold text-xl">Grade {formatGradeName(grade.grade_name)}</h3>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
                {grade.subjects.map((subject, idx) => (
                  <button
                    key={subject.subject_id}
                    onClick={() => navigate(`/student/subjects/${subject.subject_id}`)}
                    className={`rounded-2xl border p-4 sm:p-5 text-left shadow-sm transition hover:-translate-y-0.5 hover:shadow-md min-h-[180px] sm:min-h-[195px] backdrop-blur-md ${cardTones[idx % cardTones.length]}`}
                  >
                    <div className={`mb-4 inline-flex h-10 w-10 items-center justify-center rounded-xl bg-gradient-to-br text-white shadow-sm ${iconTones[idx % iconTones.length]}`}>
                      <BookOpen className="w-4 h-4" />
                    </div>
                    <h3 className="font-semibold text-2xl sm:text-3xl text-gray-800 leading-tight tracking-tight">{subject.subject_name}</h3>
                    <p className="text-lg sm:text-xl text-indigo-600 mt-1">Grade {formatGradeName(grade.grade_name)}</p>
                    <p className="text-xs sm:text-sm text-gray-500 mt-4">{subject.subject_code || `SUB-${String(subject.subject_id).padStart(3, '0')}`}</p>

                    <div className="mt-5 inline-flex items-center gap-2 text-indigo-600 text-base sm:text-lg font-semibold hover:text-indigo-700">
                      View Details
                      <ArrowRight className="w-4 h-4" />
                    </div>
                  </button>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Summary */}
      {filteredSubjects.length > 0 && (
        <div className="bg-gradient-to-r from-violet-50/90 to-purple-50/90 border border-violet-200 rounded-2xl p-5 sm:p-6 text-gray-800 shadow-sm backdrop-blur-md">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-xl sm:text-2xl font-semibold flex items-center gap-2"><Sparkles className="w-4 h-4 text-indigo-500" /> Total Subjects</h3>
              <p className="text-gray-600 mt-1 text-sm sm:text-base">You are enrolled in <span className="text-indigo-600 font-semibold">{filteredSubjects.length}</span> subject{filteredSubjects.length !== 1 ? 's' : ''}</p>
            </div>
            <div className="text-4xl sm:text-5xl font-bold text-indigo-500">{filteredSubjects.length}</div>
          </div>
        </div>
      )}
    </div>
  );
}
